import { Routes } from '@angular/router';
import { authGuard, guestGuard } from '@core/auth/auth.guard';
import { companySessionGuard } from '@core/auth/company-session.guard';
import { rbacGuard } from '@core/rbac/rbac.guard';
import { devGuard } from '@features/dev/dev.guard';
import { paymentGuard } from '@features/payment/payment.guard';
import { ShellComponent } from '@core/layout/shell/shell.component';

export const routes: Routes = [
  // Auth routes — guest only, wrapped in auth shell layout
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('@shared/components/auth-shell/auth-shell.component').then((m) => m.AuthShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('@features/auth/auth-page/auth-page.component').then((m) => m.AuthPageComponent),
      },
      {
        path: 'reset-password/:token',
        loadComponent: () =>
          import('@features/auth/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
      },
    ],
  },
  // Public invitation landing — accessible whether or not the user is logged in
  {
    path: 'accept-invitation',
    loadComponent: () =>
      import('@features/invitations/accept-invitation/accept-invitation.component').then(
        (m) => m.AcceptInvitationComponent,
      ),
  },
  // Legacy redirects
  { path: 'forgot-password', redirectTo: 'login', pathMatch: 'full' },
  // Fullscreen design previews — no shell
  {
    path: 'dev/designs',
    canActivate: [devGuard],
    loadChildren: () => import('@features/dev/dev.routes').then((m) => m.devDesignRoutes),
  },
  // Backoffice (Shiftat staff) — its own session, auth pages and shell
  {
    path: 'backoffice',
    loadChildren: () => import('@features/backoffice/backoffice.routes').then((m) => m.backofficeRoutes),
  },
  // Partner Portal — its own session, auth pages and shell
  {
    path: 'partner',
    loadChildren: () => import('@features/partner-portal/partner-portal.routes').then((m) => m.partnerRoutes),
  },
  // Protected routes wrapped in shell layout
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard],
    children: [
      // Company selection — authenticated, before a company session exists.
      // Rendered inside the shell so the sidebar is present.
      {
        path: 'select-company',
        loadComponent: () =>
          import('@features/auth/select-company/select-company.component').then((m) => m.SelectCompanyComponent),
      },
      {
        path: 'recruitment',
        loadChildren: () => import('@features/recruitment/recruitment.routes').then((m) => m.recruitmentRoutes),
      },
      {
        path: 'payment/:checkoutId',
        canActivate: [paymentGuard],
        loadComponent: () => import('@features/payment/payment.component').then((m) => m.PaymentComponent),
      },
      {
        path: 'settings',
        canActivate: [companySessionGuard],
        loadChildren: () => import('@features/settings/settings.routes').then((m) => m.settingsRoutes),
      },
      {
        path: 'admin',
        canActivate: [companySessionGuard, rbacGuard('admin.users.view')],
        loadChildren: () => import('@features/admin/admin.routes').then((m) => m.adminRoutes),
      },
      {
        path: 'hr',
        canActivate: [companySessionGuard],
        loadChildren: () => import('@features/hr/hr.routes').then((m) => m.hrRoutes),
      },
      {
        path: 'hr-planning',
        canActivate: [companySessionGuard],
        loadChildren: () => import('@features/hr-planning/hr-planning.routes').then((m) => m.hrPlanningRoutes),
      },
      {
        path: 'dev',
        canActivate: [devGuard],
        loadChildren: () => import('@features/dev/dev.routes').then((m) => m.devRoutes),
      },
      { path: '', redirectTo: 'recruitment/roles', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: 'login' },
];
